import * as React from "react";
import { InputAdornment, TextField, Tooltip } from "@mui/material";
import { observer } from "mobx-react";
import * as _ from "lodash";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import { useRootStore } from "../../../store/contexts";
import { VisibilityIcon } from "../../../icons";
import { IconButton } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { FieldLabelWithHelp } from "./FieldHelpIcon";

export const idForAdding = "add-new";

export type InputReferenceType = {
  displayId: string;
  id: string;
  inputValue?: string;
  [key: string]: any;
};
const filter = createFilterOptions<InputReferenceType>();

export type InputReferenceProps = {
  id: string;
  selectedValue?: InputReferenceType | null;
  onChange: (value: string) => void;
  onSelect: (value: InputReferenceType | null) => void;
  options?: InputReferenceType[];
  optionsMap?: (option: any, backgroundColor: string) => React.ReactElement;
  miniFormMode?: "inline" | "tooltip";
  error?: boolean;
  helperText?: string;
  label?: string;
  icon?: React.ReactElement;
  onView?: () => void;
  autoFocus?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  description?: string;
  canCreate?: boolean;
  nullText?: string;
  renderCreateDialog?: (props: {
    open: boolean;
    initialValue: string;
    onCreated: (value: { displayId: string; id: string }) => void;
    onCancelled: () => void;
  }) => React.ReactNode;
};

export const InputReferenceMiniForm: React.FC<InputReferenceProps> = observer(
  ({
    onChange,
    onSelect,
    selectedValue,
    options = [],
    error,
    helperText,
    label,
    onView,
    autoFocus = false,
    optionsMap,
    nullText,
    miniFormMode = "inline",
    disabled = false,
    hidden = false,
    description,
    canCreate = true,
    renderCreateDialog,
  }) => {
    const theme = useTheme();
    const rootStore = useRootStore();
    const { langStore } = rootStore;
    const [createDialogOpen, setCreateDialogOpen] = React.useState(false);
    const [createDialogInitialValue, setCreateDialogInitialValue] =
      React.useState("");
    const [inputValue, setInputValue] = React.useState("");

    if (hidden) return null;

    const baseLabel =
      nullText && !selectedValue ? `${label} (${nullText})` : label;

    return (
      <>
        <Autocomplete
          disabled={disabled}
          autoComplete={false}
          options={options}
          value={selectedValue}
          inputValue={inputValue}
          onInputChange={(_e, newInputValue) => setInputValue(newInputValue)}
          slotProps={{
            popper: {
              sx: { zIndex: 10300 },
            },
          }}
          isOptionEqualToValue={(option: any, value: any) =>
            option.id === value.id
          }
          onChange={(_event: any, newValue: any) => {
            if (newValue?.id === "add-new" && renderCreateDialog) {
              const notFound = /".*?"/.exec(newValue.displayId);
              setCreateDialogInitialValue(
                notFound ? notFound[0].replaceAll('"', "") : ""
              );
              setCreateDialogOpen(true);
              setInputValue("");
              return;
            }
            onSelect(newValue);
          }}
          sx={{
            m: 1,
            width: 500,
            "& .MuiInputBase-root": {
              paddingRight: "50px !important",
            },
          }}
          getOptionLabel={(option: any) => option.displayId}
          noOptionsText={`${langStore.noDataMessage}`}
          renderInput={(params) => {
            return (
              <TextField
                {...params}
                autoFocus={autoFocus}
                size="small"
                variant="outlined"
                autoComplete="one-time-code"
                fullWidth
                error={error}
                label={
                  <FieldLabelWithHelp
                    label={baseLabel}
                    description={description}
                  />
                }
                InputLabelProps={description ? { shrink: true } : undefined}
                helperText={helperText}
                onChange={_.debounce(
                  (event) => onChange(event.target.value),
                  500
                )}
                placeholder={
                  !label ? `${langStore.searchCommand}...` : baseLabel
                }
                InputProps={{
                  ...params.InputProps,
                  autoComplete: "one-time-code",
                  endAdornment: onView ? (
                    <>
                      <InputAdornment position="end">
                        <IconButton
                          onClick={onView}
                          sx={{ color: "text.secondary" }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                      {params.InputProps.endAdornment}
                    </>
                  ) : (
                    params.InputProps.endAdornment
                  ),
                }}
              />
            );
          }}
          filterOptions={(options, params) => {
            const filtered = filter(options, params);
            const { inputValue } = params;
            if (canCreate && options.length === 0 && inputValue !== "") {
              return [
                {
                  id: "add-new",
                  inputValue,
                  displayId: `${langStore.addCommand} "${inputValue}"`,
                },
              ];
            }
            return filtered;
          }}
          renderOption={(props, option) => {
            const index = props.id ? +props.id.split("-").slice(-1) % 2 : 1;
            const backgroundColor =
              index === 0
                ? theme.palette.background.paper
                : theme.palette.action.hover;
            let extraStyle = {};
            const adding = option.id === idForAdding;
            if (adding) {
              extraStyle = { color: "darkGreen", fontWeight: 700 };
            }

            let optionFragment;
            if (adding) {
              optionFragment = (
                <span style={{ ...extraStyle }}>{option.displayId}</span>
              );
            } else if (optionsMap && miniFormMode === "tooltip") {
              optionFragment = (
                <Tooltip
                  title={optionsMap(option, theme.palette.background.paper)}
                  placement="right"
                  arrow
                  slotProps={{
                    popper: {
                      sx: { zIndex: 10300 },
                    },
                    tooltip: {
                      sx: { maxWidth: "none" },
                    },
                  }}
                >
                  <span>{option.displayId}</span>
                </Tooltip>
              );
            } else if (optionsMap) {
              optionFragment = optionsMap(option, backgroundColor);
            } else {
              optionFragment = <div>{option.id}</div>;
            }

            return (
              <li {...props} key={option.id}>
                <div>{optionFragment}</div>
              </li>
            );
          }}
        />
        {renderCreateDialog &&
          renderCreateDialog({
            open: createDialogOpen,
            initialValue: createDialogInitialValue,
            onCreated: (value) => {
              setCreateDialogOpen(false);
              onSelect(value);
            },
            onCancelled: () => {
              setCreateDialogOpen(false);
            },
          })}
      </>
    );
  }
);
